import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Minus, RotateCcw, Target, CheckCircle2, CircleDashed } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { http } from "@/lib/api";
import { LearningProgress } from "@/components/LearningChecklist";

const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const avg = (o) => Math.round(Object.values(o).reduce((a, b) => a + b, 0) / Object.values(o).length);

const Delta = ({ d }) => {
  const r = Math.round(d);
  if (r > 0) return <span className="inline-flex items-center gap-1 text-[#3E6B5C] font-mono-ui text-sm"><TrendingUp size={14} />+{r}</span>;
  if (r < 0) return <span className="inline-flex items-center gap-1 text-[#C05630] font-mono-ui text-sm"><TrendingDown size={14} />{r}</span>;
  return <span className="inline-flex items-center gap-1 text-slate-400 font-mono-ui text-sm"><Minus size={14} />0</span>;
};

const CompareRow = ({ label, a, b }) => (
  <div className="grid grid-cols-[1fr_auto] gap-3 items-center py-2.5 border-b border-[#1C242B]/8 last:border-0">
    <div>
      <p className="text-sm text-slate-700">{label}</p>
      <div className="relative h-2 mt-1.5 rounded-full bg-slate-200/70 overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-[#1C242B]/25 rounded-full" style={{ width: `${a}%` }} />
        <div className="absolute inset-y-0 left-0 h-1 top-0.5 bg-[#D96B43] rounded-full transition-[width] duration-700" style={{ width: `${b}%` }} />
      </div>
    </div>
    <div className="text-right w-24"><span className="font-mono-ui text-xs text-slate-500">{Math.round(a)}→{Math.round(b)} </span><Delta d={b - a} /></div>
  </div>
);

const goalNow = (snap, g) => (g.kind === "section" ? snap.sections[g.key]?.pct : snap.intelligences[g.key]) ?? 0;

const GoalCheck = ({ prev, cur }) => (
  <div className="rounded-2xl bg-[#1C242B] text-[#FAF8F5] p-6" data-testid="goal-check-panel">
    <div className="flex items-center gap-2"><Target size={16} className="text-[#F0A584]" /><p className="font-mono-ui text-xs tracking-[0.2em] text-[#F0A584]">GOAL CHECK</p></div>
    <div className="mt-4 grid sm:grid-cols-3 gap-4">
      {prev.goals.map((g) => {
        const now = Math.round(goalNow(cur, g));
        const hit = now >= g.target;
        const pct = Math.max(0, Math.min(100, ((now - g.baseline) / Math.max(1, g.target - g.baseline)) * 100));
        return (
          <div key={g.key} className="rounded-xl border border-white/15 p-4" data-testid={`goal-check-${g.key}`}>
            <p className="text-sm text-slate-300">{g.label}</p>
            <p className="mt-1 font-display text-2xl font-bold">{now}<span className="text-sm font-normal text-slate-400"> / goal {g.target}</span></p>
            <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#F0A584]" style={{ width: `${pct}%` }} /></div>
            <p className={`mt-2 text-xs inline-flex items-center gap-1 ${hit ? "text-[#9FD3BF]" : "text-slate-400"}`} data-testid={`goal-status-${g.key}`}>
              {hit ? <><CheckCircle2 size={13} /> Goal reached</> : <><CircleDashed size={13} /> {g.target - now} to go (started at {g.baseline})</>}
            </p>
          </div>
        );
      })}
    </div>
  </div>
);

const Panel = ({ title, children, testId }) => (
  <div className="rounded-2xl bg-white border border-[#1C242B]/10 p-6" data-testid={testId}>
    <p className="overline-label mb-3">{title}</p>
    {children}
  </div>
);

export const GrowthSection = ({ token, favorites }) => {
  const [hist, setHist] = useState(null);
  const [base, setBase] = useState(0);
  useEffect(() => {
    http.get(`/results/${token}/history`).then((r) => { setHist(r.data.history); setBase(Math.max(0, r.data.history.length - 2)); }).catch(() => setHist([]));
  }, [token]);

  if (!hist) return null;
  if (hist.length < 2) {
    return (
      <>
      {favorites?.length > 0 && <div className="mt-16"><LearningProgress favorites={favorites} /></div>}
      <section className="mt-16 rounded-2xl border border-dashed border-[#1C242B]/20 p-8 flex flex-col md:flex-row md:items-center gap-5 justify-between" data-testid="growth-empty">
        <div>
          <p className="overline-label">Track your growth</p>
          <p className="font-display text-xl font-semibold mt-2">Retake the test in a few months to see how your strengths change.</p>
          <p className="text-sm text-slate-600 mt-1">Save your private link: your next attempt will be compared with this one automatically.</p>
        </div>
        <Link to={`/assessment?retake=${token}`} data-testid="growth-retake-btn" className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#1C242B] text-white px-5 py-3 text-sm font-semibold hover:bg-[#D96B43] transition-colors">
          <RotateCcw size={16} /> Retake later
        </Link>
      </section>
      </>
    );
  }

  const prev = hist[base], cur = hist[hist.length - 1];
  const trend = hist.map((h, i) => ({ name: `#${i + 1}`, Aptitude: avg(Object.fromEntries(Object.entries(h.sections).map(([k, v]) => [k, v.pct]))), Intelligences: avg(h.intelligences), Knowledge: Math.round(h.knowledge_overall) }));
  const prevIds = new Set(prev.top_careers.map((c) => c.id));

  return (
    <section className="mt-16" data-testid="growth-section">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-label">Progress comparison</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">How you've grown</h2>
          <p className="text-slate-600 mt-2">Test {hist.length} of {hist.length}, taken {fmt(cur.completed_at)}. Holland code <b>{prev.holland_code}</b> → <b data-testid="growth-holland-now">{cur.holland_code}</b>.</p>
        </div>
        {hist.length > 2 && (
          <label className="text-sm text-slate-600">Compare with{" "}
            <select data-testid="growth-baseline-select" value={base} onChange={(e) => setBase(Number(e.target.value))} className="ml-1 rounded-full border border-[#1C242B]/15 bg-white px-3 py-1.5">
              {hist.slice(0, -1).map((h, i) => <option key={h.token} value={i}>Test #{i + 1} · {fmt(h.completed_at)}</option>)}
            </select>
          </label>
        )}
      </div>
      <div className="mt-6 grid grid-cols-12 gap-5">
        {prev.goals?.length > 0 && <div className="col-span-12"><GoalCheck prev={prev} cur={cur} /></div>}
        {favorites?.length > 0 && <div className="col-span-12"><LearningProgress favorites={favorites} /></div>}
        <div className="col-span-12 lg:col-span-7">
          <Panel title="Aptitude tests: before vs now" testId="growth-aptitudes">
            {Object.entries(cur.sections).filter(([k]) => prev.sections[k]).map(([k, v]) => <CompareRow key={k} label={v.title} a={prev.sections[k].pct} b={v.pct} />)}
          </Panel>
        </div>
        <div className="col-span-12 lg:col-span-5">
          <Panel title="Intelligences" testId="growth-intelligences">
            {Object.entries(cur.intelligences).map(([k, v]) => <CompareRow key={k} label={k} a={prev.intelligences[k]} b={v} />)}
          </Panel>
        </div>
        {hist.length > 2 && (
          <div className="col-span-12">
            <Panel title="Trend across all your tests" testId="growth-trend-chart">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trend}>
                    <CartesianGrid stroke="#1C242B14" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="Aptitude" stroke="#D96B43" strokeWidth={2.5} />
                    <Line type="monotone" dataKey="Intelligences" stroke="#3E6B5C" strokeWidth={2.5} />
                    <Line type="monotone" dataKey="Knowledge" stroke="#2563EB" strokeWidth={2.5} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>
        )}
        <div className="col-span-12 md:col-span-6">
          <Panel title="Interests (RIASEC)" testId="growth-riasec">
            {Object.entries(cur.riasec).map(([k, v]) => <CompareRow key={k} label={{ R: "Realistic", I: "Investigative", A: "Artistic", S: "Social", E: "Enterprising", C: "Conventional" }[k]} a={prev.riasec[k]} b={v} />)}
          </Panel>
        </div>
        <div className="col-span-12 md:col-span-6">
          <Panel title="Top careers: then vs now" testId="growth-top-careers">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <ol className="space-y-2"><li className="text-xs font-mono-ui text-slate-500">THEN · {fmt(prev.completed_at)}</li>{prev.top_careers.map((c) => <li key={c.id} className="text-slate-600">{c.title}</li>)}</ol>
              <ol className="space-y-2"><li className="text-xs font-mono-ui text-slate-500">NOW</li>{cur.top_careers.map((c) => (
                <li key={c.id} className="text-[#1C242B] font-medium">{c.title}{!prevIds.has(c.id) && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-[#D96B43]">new</span>}</li>
              ))}</ol>
            </div>
          </Panel>
        </div>
      </div>
    </section>
  );
};
